/**
 * ThemeContext — Light/Dark for the user-facing app.
 *
 * Contract (design decision D1, resolved):
 *   • Light is the default for anyone without a stored preference.
 *   • The user's explicit choice ALWAYS wins over the OS preference.
 *   • The choice is persisted client-side only: no backend, no API.
 *   • The theme is applied to <html data-theme> (plus the `dark` class so
 *     Tailwind's `dark:` variant keeps working) by the inline boot script in
 *     `public/index.html`, before first paint — that is what prevents the
 *     flash of the wrong theme. This provider only keeps React in sync.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

export const THEME_STORAGE_KEY = "etf-theme";
export const THEMES = ["light", "dark"];
export const DEFAULT_THEME = "light";

const ThemeContext = createContext(null);

function readStoredTheme() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function applyTheme(theme) {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.classList.toggle("dark", theme === "dark");
  // browser chrome (address bar, PWA) follows the active theme
  const meta = document.getElementById("theme-color-meta");
  if (meta) meta.setAttribute("content", theme === "dark" ? "#04121E" : "#F8FAFD");
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => readStoredTheme() || DEFAULT_THEME);

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Private mode: the theme still applies for this session.
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(() => ({
    theme,
    isDark: theme === "dark",
    setTheme: (next) => setTheme(THEMES.includes(next) ? next : DEFAULT_THEME),
    toggleTheme,
  }), [theme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // The Admin Console renders inside its own compatibility scope and does
    // not need the switch: a neutral fallback keeps it decoupled.
    return {
      theme: DEFAULT_THEME, isDark: false, setTheme: () => {}, toggleTheme: () => {},
    };
  }
  return ctx;
}
